/**
 * ステータス変更モーダル（data 属性駆動）
 *
 * 「ラジオで遷移先を選ぶ → 現在状態と違うときだけ実行可能 → 確認ダイアログの文言を
 *  選択に応じて差し替えて開く → 確認側の submit で POST」を汎用化した部品
 * （checkpoint-progress 05_ui.md §2.5.4）。
 *
 * マークアップ規約:
 *   <div class="lms-overlay" id="cp-modal-1-2" data-lms-modal hidden
 *        data-lms-status-modal
 *        data-lms-status-current="IN_PROGRESS"
 *        data-lms-status-confirm="cp-confirm-1-2"
 *        data-lms-status-section="変数と型">
 *     <input type="radio" name="status" value="NOT_STARTED" data-lms-status-radio>
 *     …
 *     <button type="button" data-lms-status-submit disabled>変更する</button>
 *   </div>
 */
import { openModal } from "./modal.js";

type ProgressStatusValue = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

/** 確認ダイアログの見出し・本文テーブル（サーバー側テンプレートの文言と一致させる）。 */
const CONFIRM_TEXT: Record<ProgressStatusValue, { title: string; body: (section: string) => string }> = {
  COMPLETED: {
    title: "完了にしますか？",
    body: (section) =>
      `「${section}」を完了済みにします。カリキュラム順で次のチェックポイントが未完了の場合は実施中になります。`,
  },
  NOT_STARTED: {
    title: "未完了に戻しますか？",
    body: (section) => `「${section}」を未完了に戻します。他のチェックポイントの状態は変わりません。`,
  },
  IN_PROGRESS: {
    title: "実施中にしますか？",
    body: (section) => `「${section}」を実施中にします。実施中だった他のチェックポイントは未完了に戻ります。`,
  },
};

function isProgressStatusValue(value: string): value is ProgressStatusValue {
  return value === "NOT_STARTED" || value === "IN_PROGRESS" || value === "COMPLETED";
}

export function initStatusModal(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-lms-status-modal]").forEach((el) => {
    if (el.dataset["lmsStatusReady"] === "1") return;
    el.dataset["lmsStatusReady"] = "1";

    const current = el.dataset["lmsStatusCurrent"];
    const sectionTitle = el.dataset["lmsStatusSection"] ?? "";
    const confirmId = el.dataset["lmsStatusConfirm"];
    const confirm = confirmId ? document.getElementById(confirmId) : null;
    const submitBtn = el.querySelector<HTMLButtonElement>("[data-lms-status-submit]");
    const radios = Array.from(el.querySelectorAll<HTMLInputElement>("[data-lms-status-radio]"));

    if (!current || !confirm || !submitBtn || radios.length === 0) return;

    const sync = (): void => {
      const checkedRadio = radios.find((r) => r.checked) ?? null;
      radios.forEach((r) => {
        r.closest(".lms-radio-card")?.classList.toggle("is-selected", r === checkedRadio);
      });
      const selected = checkedRadio?.value ?? null;
      submitBtn.disabled = selected === null || selected === current;
    };

    const openConfirm = (): void => {
      const selected = radios.find((r) => r.checked)?.value ?? null;
      if (selected === null || selected === current || !isProgressStatusValue(selected)) return;

      const text = CONFIRM_TEXT[selected];
      const titleEl = confirm.querySelector<HTMLElement>("[data-lms-status-confirm-title]");
      const bodyEl = confirm.querySelector<HTMLElement>("[data-lms-status-confirm-body]");
      if (titleEl) titleEl.textContent = text.title;
      if (bodyEl) bodyEl.textContent = text.body(sectionTitle);

      openModal(confirm);
    };

    sync();
    radios.forEach((r) => r.addEventListener("change", sync));
    submitBtn.addEventListener("click", openConfirm);
  });
}
